import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import * as PROFILE from '../../../constants/profile';
import { useAuth } from '../../../hooks/useAuth';

const periods = [
  { label: 'Diario', value: 1 },
  { label: 'Semanal', value: 2 },
  { label: 'Mensual', value: 3 }
];

function MetricsReport() {
  const { hasPermission } = useAuth();
  const [periodId, setPeriodId] = useState(1);
  const [report, setReport] = useState([]);
  const [periodColumns, setPeriodColumns] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    value: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [loading, setLoading] = useState(false);

  const getMetricsReport = (selectedPeriodId = periodId) => {
    setLoading(true);
    fetch(`${process.env.REACT_APP_API_URL}/adminPanel/mty`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ periodId: selectedPeriodId })
    })
      .then(response => response.json())
      .then(data => {
        if (data.responseCode === 'OK') {
          const responseObject = data.responseObject || {};
          const columns = (responseObject.periods || []).map((period, index) => ({
            field: `period_${index}`,
            label: period.label
          }));
          const rows = (responseObject.metrics || []).map(metric => ({
            description: metric.description,
            valueType: metric.valueType,
            ...columns.reduce((values, column, index) => ({
              ...values,
              [column.field]: metric.values?.[index] ?? null
            }), {})
          }));

          setPeriodColumns(columns);
          setReport(rows);
          setFilters({
            global: { value: null, matchMode: FilterMatchMode.CONTAINS },
            description: { value: null, matchMode: FilterMatchMode.CONTAINS },
            ...columns.reduce((columnFilters, column) => ({
              ...columnFilters,
              [column.field]: { value: null, matchMode: FilterMatchMode.CONTAINS }
            }), {})
          });
          setGlobalFilter('');
        } else {
          setPeriodColumns([]);
          setReport([]);
        }
      })
      .finally(() => setLoading(false));
  };

  const exportAsExcel = () => {
    import('xlsx').then(xlsx => {
      const rows = report.map(row => [
        row.description,
        ...periodColumns.map(column => row[column.field])
      ]);
      const worksheet = xlsx.utils.aoa_to_sheet([
        ['Descripción', ...periodColumns.map(column => column.label)],
        ...rows
      ]);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
      import('file-saver').then(module => {
        module.default.saveAs(
          new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }),
          `ReporteDeMetricas_${new Date().getTime()}.xlsx`
        );
      });
    });
  };

  const formatValue = (value, valueType) => {
    if (value === null || value === undefined) return '';
    if (valueType === 'CURRENCY') {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN'
      }).format(value);
    }
    return new Intl.NumberFormat('es-MX').format(value);
  };

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;
    setFilters({ ...filters, global: { ...filters.global, value } });
    setGlobalFilter(value);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <article>
      <Row>
        <Col><h1 className="app-font">Reporte de Métricas</h1></Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Período</Form.Label>
            <Dropdown
              value={periodId}
              options={periods}
              onChange={(event) => {
                setPeriodId(event.value);
                getMetricsReport(event.value);
              }}
              className="w-100"
              placeholder="Seleccione un período"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Button type="button" icon="pi pi-search" onClick={getMetricsReport} className="p-button-raised p-button-sm p-button-primary" label="Buscar" />
        </Col>
      </Row>
      <Row className="pt-2">
        <Col>
          <div className="card">
            <DataTable
              value={report}
              paginator
              className="p-datatable-customers"
              showGridlines
              stripedRows
              rows={50}
              rowsPerPageOptions={[10, 25, 50]}
              filters={filters}
              filterDisplay="menu"
              loading={loading}
              responsiveLayout="scroll"
              globalFilterFields={['description', ...periodColumns.map(column => column.field)]}
              header={(
                <Row>
                  <Col>
                    <span className="p-input-icon-left">
                      <i className="pi pi-filter" />
                      <InputText value={globalFilter} onChange={onGlobalFilterChange} placeholder="Filtrar" className="p-inputtext-sm" />
                    </span>
                  </Col>
                  <Col>
                    {hasPermission(PROFILE.PERMISSIONS.DOWNLOAD_REPORTS) && (
                      <Button type="button" icon="pi pi-file-excel" onClick={exportAsExcel} className="p-button-raised p-button-sm p-button-success mr-2 float-end" data-pr-tooltip="XLS" label="Exportar" />
                    )}
                  </Col>
                </Row>
              )}
              emptyMessage="No records found."
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            >
              <Column
                field="description"
                header="Descripción"
                sortable
                filter
                filterPlaceholder="Buscar por Descripción"
                headerStyle={{ minWidth: '260px' }}
                style={{ minWidth: '260px' }}
              />
              {periodColumns.map(column => (
                <Column
                  key={column.field}
                  field={column.field}
                  header={column.label}
                  sortable
                  filter
                  filterPlaceholder={`Buscar por ${column.label}`}
                  headerStyle={{ minWidth: '160px', whiteSpace: 'nowrap', textAlign: 'center' }}
                  style={{ minWidth: '160px', textAlign: 'right' }}
                  body={(rowData) => formatValue(rowData[column.field], rowData.valueType)}
                />
              ))}
            </DataTable>
          </div>
        </Col>
      </Row>
    </article>
  );
}

export default MetricsReport;
